import { getOrCreateBlobUrl } from '@/shared/lib/blobUrlCache';

import { computeContainSize, computeCoverRect } from './computeCoverRect';
import type { ResizeWorkerRequest, ResizeWorkerResponse } from './imageResize.worker';

export type ResizeFormat = 'auto' | 'webp' | 'preserve';

// 'cover' crops to fill the target box (matches CSS object-fit: cover — the
// default, since most tiered/boxed thumbnails are displayed that way).
// 'contain' preserves the full source content, scaled to fit within the box
// with no cropping (matches CSS object-fit: contain).
export type ResizeFit = 'cover' | 'contain';

export interface ResizeRequest {
  cacheKey: string;
  sourceBlob: Blob;
  targetWidth: number;
  targetHeight?: number;
  fit?: ResizeFit;
  format: ResizeFormat;
}

export interface ResizeResult {
  url: string;
  blob: Blob;
}

// Sized to comfortably hold every thumbnail visible at once in an image-heavy
// virtualized grid (viewport + overscan) plus recently scrolled-past rows, so an
// on-screen thumbnail is effectively never evicted (and its blob URL revoked)
// while still displayed. The byte cap remains the real memory guard.
const MAX_ENTRIES = 512;
const MAX_BYTES = 48 * 1024 * 1024;
const DEFAULT_QUALITY = 0.85;

const supportsOffscreen =
  typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap !== 'undefined';

const NON_RESIZABLE_MIMES = new Set(['image/svg+xml', 'image/gif', 'application/pdf']);

export const isResizableMime = (mime: string): boolean => {
  if (NON_RESIZABLE_MIMES.has(mime)) return false;
  return mime.startsWith('image/');
};

export const computeResizeTarget = (
  logicalWidth: number,
  logicalHeight?: number,
): { width: number; height?: number; dpr: number } => {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return {
    width: Math.max(1, Math.round(logicalWidth * dpr)),
    height: logicalHeight != null ? Math.max(1, Math.round(logicalHeight * dpr)) : undefined,
    dpr,
  };
};

interface CacheEntry {
  blob: Blob;
  url: string;
  bytes: number;
}

const cache = new Map<string, CacheEntry>();
let cacheBytes = 0;

const cacheGet = (key: string): CacheEntry | undefined => {
  const entry = cache.get(key);
  if (entry) {
    cache.delete(key);
    cache.set(key, entry);
  }
  return entry;
};

const evictOne = () => {
  const oldest = cache.keys().next();
  if (oldest.done) return;
  const key = oldest.value;
  const entry = cache.get(key);
  if (!entry) return;
  cache.delete(key);
  cacheBytes -= entry.bytes;
  URL.revokeObjectURL(entry.url);
};

const cacheSet = (key: string, blob: Blob): CacheEntry => {
  const existing = cache.get(key);
  if (existing) {
    cache.delete(key);
    cacheBytes -= existing.bytes;
    URL.revokeObjectURL(existing.url);
  }
  const entry: CacheEntry = {
    blob,
    url: URL.createObjectURL(blob),
    bytes: blob.size,
  };
  cache.set(key, entry);
  cacheBytes += blob.size;
  while (cache.size > MAX_ENTRIES || cacheBytes > MAX_BYTES) {
    const sizeBefore = cache.size;
    evictOne();
    if (cache.size === sizeBefore) break;
  }
  return entry;
};

const inFlight = new Map<string, Promise<ResizeResult>>();

let worker: Worker | null = null;
const pending = new Map<string, (res: ResizeWorkerResponse) => void>();
let nextId = 0;

const ensureWorker = (): Worker => {
  if (worker) return worker;
  worker = new Worker(new URL('./imageResize.worker.ts', import.meta.url), { type: 'module' });
  worker.addEventListener('message', (e: MessageEvent<ResizeWorkerResponse>) => {
    const cb = pending.get(e.data.id);
    if (!cb) return;
    pending.delete(e.data.id);
    cb(e.data);
  });
  worker.addEventListener('error', () => {
    worker?.terminate();
    worker = null;
    pending.forEach((cb, id) => cb({ id, ok: false, error: 'worker crashed' }));
    pending.clear();
  });
  return worker;
};

const runInWorker = (req: ResizeWorkerRequest): Promise<ResizeWorkerResponse> => {
  const w = ensureWorker();
  return new Promise((resolve) => {
    pending.set(req.id, resolve);
    w.postMessage(req);
  });
};

const runOnMainThread = async (req: ResizeWorkerRequest): Promise<ResizeWorkerResponse> => {
  try {
    const url = URL.createObjectURL(req.blob);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('fallback: image decode failed'));
      });

      let sx = 0;
      let sy = 0;
      let sw = img.naturalWidth;
      let sh = img.naturalHeight;
      let dw = req.width;
      let dh: number;

      if (req.height == null) {
        dh = Math.round(dw * (img.naturalHeight / img.naturalWidth));
      } else if (req.fit === 'contain') {
        // Preserve the full source content, scaled to fit within the box.
        ({ width: dw, height: dh } = computeContainSize(
          img.naturalWidth,
          img.naturalHeight,
          req.width,
          req.height,
        ));
      } else {
        // A fixed W x H box was requested — crop to its aspect ratio first
        // (matching CSS `object-fit: cover`) instead of stretching.
        ({ sx, sy, sw, sh } = computeCoverRect(
          img.naturalWidth,
          img.naturalHeight,
          req.width,
          req.height,
        ));
        dh = req.height;
      }

      const canvas = document.createElement('canvas');
      canvas.width = dw;
      canvas.height = dh;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return { id: req.id, ok: false, error: 'fallback: 2d context unavailable' };
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);

      const outBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), req.outMime, req.quality);
      });
      if (!outBlob) {
        return { id: req.id, ok: false, error: 'fallback: toBlob returned null' };
      }
      return { id: req.id, ok: true, blob: outBlob, width: dw, height: dh };
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    return { id: req.id, ok: false, error: err instanceof Error ? err.message : String(err) };
  }
};

const decideOutputMime = (sourceMime: string, format: ResizeFormat): string => {
  if (format === 'preserve') return sourceMime;
  return 'image/webp';
};

let warnedTaintedOnce = false;

export const getResizedImage = async (req: ResizeRequest): Promise<ResizeResult> => {
  const cached = cacheGet(req.cacheKey);
  if (cached) return { url: cached.url, blob: cached.blob };

  const existing = inFlight.get(req.cacheKey);
  if (existing) return existing;

  const id = `r${++nextId}`;
  const outMime = decideOutputMime(req.sourceBlob.type, req.format);

  const workerReq: ResizeWorkerRequest = {
    id,
    blob: req.sourceBlob,
    width: req.targetWidth,
    height: req.targetHeight,
    fit: req.fit ?? 'cover',
    outMime,
    quality: DEFAULT_QUALITY,
  };

  const promise = (supportsOffscreen ? runInWorker(workerReq) : runOnMainThread(workerReq)).then(
    (res) => {
      inFlight.delete(req.cacheKey);
      if (!res.ok) {
        if (!warnedTaintedOnce && /SecurityError|tainted/i.test(res.error)) {
          warnedTaintedOnce = true;
          // eslint-disable-next-line no-console
          console.warn(
            '[imageResize] resize failed (possibly tainted canvas / opaque response); ' +
              'falling back to source blob. Future failures will be silent.',
          );
        }
        // Route the fallback through the shared blob-URL cache so it's
        // deduplicated per source blob and revoked by the FinalizationRegistry
        // when that blob is GC'd — a raw createObjectURL here leaked one
        // full-res URL per unresizable image for the rest of the session.
        return { url: getOrCreateBlobUrl(req.sourceBlob), blob: req.sourceBlob };
      }
      const entry = cacheSet(req.cacheKey, res.blob);
      return { url: entry.url, blob: entry.blob };
    },
  );

  inFlight.set(req.cacheKey, promise);
  return promise;
};

// Synchronous, side-effect-free cache lookup — returns a previously resized
// image immediately if present, so callers can paint it without waiting on (or
// even fetching) the full-resolution source. Deliberately does NOT touch LRU
// recency, keeping it safe to call during render.
export const peekResizedImage = (cacheKey: string): ResizeResult | undefined => {
  const entry = cache.get(cacheKey);
  return entry ? { url: entry.url, blob: entry.blob } : undefined;
};

export const _imageResizeTestUtils = {
  clearCache: () => {
    cache.forEach((entry) => URL.revokeObjectURL(entry.url));
    cache.clear();
    cacheBytes = 0;
    inFlight.clear();
  },
  stats: () => ({ entries: cache.size, bytes: cacheBytes, inFlight: inFlight.size }),
};
